// ── BEGINNING FARMER / RANCHER — DERIVED FROM THE SUBSIDY, NEVER STATED BY M13 ──
//
// Kevin, 2026-09-03: "are you able to currently see through M13 who currently
// qualifies for BFR?" — and then "A BFR flag would be good, so when quoting we know
// to use that."
//
// ⚠ M13 CARRIES NO BFR FIELD. Checked across every mirrored record type: nothing for
// beginning or veteran farmer status, on either AIP. So this is a DERIVATION and every
// surface must label it as one. It is not the AIP saying "this person is a BFR"; it is
// the AIP's own stamped subsidy being arithmetically impossible for anyone who is not.
//
// ── WHY SCO/ECO ARE THE PROBE ────────────────────────────────────────────────────
// The MPCI subsidy moves with BOTH coverage level and unit structure, so reading a bump
// out of it needs a lookup table and a `unit_structure` this repo already documents as
// unreliably populated. **SCO and ECO carry a FLAT rate** — measured 0.8000 on 775 of
// 832 CY2026 rows across 169 farms — so any elevation above it is the bump and nothing
// else.
//
// ⚠ FOUR STEPS, NOT TWO — corrected 2026-09-30. RMA's schedule after the One Big
// Beautiful Bill Act is **+15 for the 1st–2nd BFR crop years, +13 for the 3rd, +11 for
// the 4th, +10 for the 5th–10th**, the window itself having gone from 5 crop years to
// 10. This file previously modelled two tiers, which is right at both ends and wrong in
// the middle:
//
//     0.80 -> standard   0.90 -> +10   0.91 -> +11   0.93 -> +13   0.95 -> +15
//
// The gap was an INFERENCE that fitted every case we had — the only BFR fixture
// (Atwater, cl 0.50) is a 5th-year-or-later grower, so +10 matched it to the penny and
// nothing in the set was in year 3 or 4. The live book was showing the missing step all
// along: CY2026 SCO/ECO ratios cluster at .8998–.9004 (4 policies) and **.9096–.9133
// (5 policies)**, and a 2-point tolerance rounded the second cluster into the first.
// Found when FIRE CREEK FARMS' R&H Coverage Analyzer quoted 82% where this engine said
// 81%. Rule zero: an inference that fits the data is not evidence.
//
// ⚠ IT IS PER **POLICY**, NOT PER FARM, and that is not a nicety. Two farms in this
// book hold one standard policy and one BFR policy under a single name:
//
//     HOWLETT FARMS INC   MP-0927356 wheat   0.800 on every row
//                         MP-0936636 corn+soy 0.950 on every row
//
// Kevin confirmed the mechanism: Howlett transitioned to the next generation, who did
// qualify. Two insureds, one farmer name in our book — the name-collapse hazard this
// repo already records. Rolled up to the farm, Howlett reads half-BFR and neither
// number is true of anybody.
//
// ⚠ AND THE LADDER STEPS WITH THE CROP YEAR, so a status derived from a PAST year is
// never carried forward as a tier. `deriveBfr` reports the tier it OBSERVED with the
// year it observed it in; `bfrQuoteSeed` advances the benefit year and quotes the tier
// for THAT year:
//   - +13 and +11 pin a single benefit year (3rd, 4th), so they step exactly: a +11
//     observed in CY2026 is the 4th year, making CY2027 the 5th, i.e. +10.
//   - +15 spans two years and +10 spans six, so neither pins one. Those fall back to
//     the durable +10 and flag it, because quoting a bonus a grower may no longer have
//     UNDER-states their premium on a document they sign.
//
// ⚠ AND ON FIRE CREEK THE AIP'S OWN TWO ARTIFACTS DISAGREE ABOUT THE YEAR. Its CY2026
// policy was BILLED at +11 — 0.6200 against a BU-80 base of 0.51, and 0.9102 against
// the flat ECO base, two independent rows — which makes CY2026 the 4th benefit year and
// CY2027 the 5th (+10). But R&H's CY2027 Coverage Analyzer quotes +11 and prints
// "Years of BFR Benefits Received - 3 Years". One of the two is carrying a stale count,
// and nothing we hold settles which. This rule STEPS, because stepping is what the
// published schedule says, and sets `steppedDown` so the disagreement is on screen for
// a human to settle with the AIP rather than a default deciding it silently.
//
// ── THE FALLBACK, AND WHY IT IS SECOND ───────────────────────────────────────────
// 269 of 342 CY2026 crop policies carry SCO or ECO (78.7%). For the rest there is a
// non-parametric probe: compare the policy's MPCI rate against the MODAL rate for its
// own (coverage level, unit structure) across the book. It needs no external table and
// no memory of RMA's schedule — which matters, because the modal 75% basic/optional
// rate in this book is 0.600 and a from-memory 0.55 would have manufactured a +5 tier
// that does not exist. It found two real farms the flat probe could not see
// (LEMISZKO ZACHARY, MUNCH ANDREW, both 0.750 against a modal 0.600).
//
// It is second because it depends on `unit_structure`, and a cell with too few peers
// cannot establish a mode. Where neither probe answers, the result is UNKNOWN — never
// "not a BFR", because those are different statements and only one of them is safe to
// put on a quote.

const AREA_PLANS = new Set(["31", "32", "87", "88"]);   // SCO-YP/RP, ECO-YP/RP
const BASE_PLANS = new Set(["01", "02", "03", "90"]);

// The flat rate SCO and ECO are subsidised at. Measured, not recalled — and it MOVED:
// the One Big Beautiful Bill Act raised SCO from 65% to 80% for sales closing dates on
// or after 2025-07-01, so a CY2025-and-earlier row is a different baseline and this
// module refuses those rather than reading a 15-point gap that is really the reform.
const AREA_BASE_SUBSIDY = 0.80;
const AREA_BASE_FIRST_YEAR = 2026;

// Tolerance. The AIP rounds subsidy to the dollar, so a small premium lands a few
// thousandths off — Fire Creek reads 0.9099 on a $4,354 policy. Wide enough for that,
// far narrower than the 5-point gap between tiers.
// ⚠ AND IT MUST BE UNDER HALF THE GAP BETWEEN TIERS, or a rate lands inside two of
// them at once and the answer is decided by which branch is tested first. The tiers are
// 5 points apart, so anything at or above 0.025 makes 0.925 read as both. Widest real
// deviation measured in the book is 0.016 (a 0.816 standard row); Fire Creek's 0.9099
// is 0.010. 0.02 clears every observation and cannot reach the next tier.
const TOL = 0.02;
// ⚠ A SEPARATE, TIGHTER TOLERANCE FOR THE TIER LADDER. TOL is used elsewhere against the
// MODAL base-rate fallback, where the gaps are wide; the tier steps are ONE point apart,
// so anything at or above 0.005 cannot tell +10 from +11. 0.004 clears the widest
// deviation actually observed in the book (0.9133 is 0.0033 off 0.91) and cannot reach a
// neighbouring step.
const TOL_TIER = 0.004;

// ⚠ THE AIP STATES IT. P14 field 32 is the Administrative Fee Waiver Code, and RMA's
// own edit rule on that field (2026 Approved Appendix III, P14 p.20) is not ambiguous:
//   "must equal \"B\" when the Policy Producer qualifies for Beginning Farmer Rancher"
//   "must not equal \"B\" when the Policy Producer does not qualify"
//   "must equal \"G\" ... for both Beginning Farmer Rancher and Limited Resource Farmer"
// Everything below this line is an INFERENCE from the subsidy a rate implies. This is
// the carrier's own statement, so it outranks all of it. See sql/060.
//
// ⚠ IT CONFIRMS, NEVER REFUTES. The code is written per commodity and county, not on
// every coverage a policy holds (RMA's rule: it "cannot be null because another P14
// record has been submitted for the same Commodity/State/County with an Administrative
// Fee Waiver Code"). So a BLANK is not a denial and must fall through to the
// derivation — treating absence as NONE would report every beginning farmer whose
// wheat county happens not to carry the code as an ordinary producer.
const WAIVER_BFR = new Set(["B", "G"]);
const waiverOf = (r) => String(r.admin_fee_waiver_code ?? r.adminFeeWaiverCode ?? "").trim().toUpperCase();
function statedBfr(rows) {
  for (const r of rows || []) { const w = waiverOf(r); if (WAIVER_BFR.has(w)) return w; }
  return null;
}

// ⚠ FOUR STEPS, NOT TWO — corrected 2026-09-30. RMA's post-OBBBA schedule is +15 for the
// 1st–2nd BFR crop years, +13 for the 3rd, +11 for the 4th, +10 for the 5th–10th. The
// two-tier model this file shipped with was an inference that fitted every case we had,
// because the only BFR fixture (Atwater, cl 0.50) is a 5th-year-or-later grower. See the
// schedule block in src/mpciEngine.js for the source and how the gap survived.
//
// ⚠ AND THE TIERS ARE NOW ONE POINT APART, so the old TOL of 0.02 cannot separate them:
// it swallowed 0.91 into 0.90 and read five live policies a point light. The live book's
// SCO/ECO ratios cluster tightly — measured CY2026: .8998–.9004 (4 policies) and
// .9096–.9133 (5) — so the clusters are real and only the tolerance was wrong.
const TIER = { NONE: "NONE", BFR_10: "BFR_10", BFR_11: "BFR_11", BFR_13: "BFR_13", BFR_15: "BFR_15", UNKNOWN: "UNKNOWN" };
const BONUS = { NONE: 0, BFR_10: 0.10, BFR_11: 0.11, BFR_13: 0.13, BFR_15: 0.15, UNKNOWN: 0 };
// Which BFR crop year each tier NAMES. +15 spans two years and +10 spans six, so the
// year is only pinned where the tier is one of the interior steps — which is exactly
// where it matters, because those are the ones that step DOWN next year.
const TIER_YEARS = { BFR_15: [1, 2], BFR_13: [3], BFR_11: [4], BFR_10: [5, 10] };
const TIER_BY_BONUS = { 0.10: TIER.BFR_10, 0.11: TIER.BFR_11, 0.13: TIER.BFR_13, 0.15: TIER.BFR_15 };
// The schedule, as a year -> bonus ladder. Kept here as well as in mpciEngine so this
// module stays import-free (the checker loads and drives the shipped source); the two are
// asserted identical by check-bfr-status rather than by a comment.
const BONUS_BY_YEAR = { 1: 0.15, 2: 0.15, 3: 0.13, 4: 0.11, 5: 0.10, 6: 0.10, 7: 0.10, 8: 0.10, 9: 0.10, 10: 0.10 };
const LAST_BENEFIT_YEAR = 10;

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
const planOf = (r) => String(r.mpci_plan_code ?? r.mpciPlanCode ?? "").padStart(2, "0");

// Sum first, THEN divide. Averaging per-row ratios lets a $40 row outvote a $40,000 one
// and drags a policy off its own tier.
function pooled(rows) {
  let tp = 0, sub = 0, n = 0;
  for (const r of rows) {
    const t = num(r.total_premium), s = num(r.subsidy);
    if (t == null || s == null || t <= 0 || s <= 0) continue;
    tp += t; sub += s; n++;
  }
  return n ? { rate: sub / tp, n, gross: tp } : null;
}

// ⚠ NEAREST STEP, NOT FIRST MATCH — and the tolerance must be under half the gap.
// With four tiers the smallest gap is ONE point (0.90 vs 0.91), so a first-match scan at
// any tolerance ≥ 0.005 answers whichever branch is tested first: that is precisely how
// 0.9102 was read as +10. Taking the NEAREST candidate makes the answer independent of
// branch order, and TOL_TIER refuses a rate that sits between steps rather than snapping
// it to one. A rate this rule cannot place says nothing — never "standard".
// ⚠ THE BOUND IS DERIVED PER STEP, NOT CHOSEN. The steps are not evenly spaced — 0.90
// and 0.91 are ONE point apart while 0.95's nearest neighbour is two and 0.80's is ten —
// so a single tolerance is either too wide for the close pair or too tight for the rest.
// A flat 0.004 was both: it fixed 0.9102 and then read a real 0.9545 (+15) as UNKNOWN.
//
// Each step's bound is therefore `min(TOL, half the distance to its nearest OTHER step)`:
//   0.90 -> half of 0.01 = 0.005   (the close pair, where precision is needed)
//   0.91 -> 0.005
//   0.93 -> half of 0.02 = 0.010
//   0.95 -> 0.010                  (so 0.9545 reads +15, as it must)
//   0.80 -> capped by TOL at 0.02  (its nearest step is ten points away)
// Nothing is hand-tuned; change the ladder and every bound follows.
const STEP_RATES = () => [[AREA_BASE_SUBSIDY, TIER.NONE]].concat(
  Object.entries(TIER_BY_BONUS).map(([b, t]) => [AREA_BASE_SUBSIDY + Number(b), t]));

function tierFromArea(rate) {
  if (rate == null) return null;
  const steps = STEP_RATES();
  let best = null, bestD = Infinity, bestBound = 0;
  for (const [at, tier] of steps) {
    const d = Math.abs(rate - at);
    if (d >= bestD) continue;
    // half the distance to the nearest OTHER step, capped by the wide tolerance
    let gap = Infinity;
    for (const [other] of steps) if (other !== at) gap = Math.min(gap, Math.abs(other - at));
    bestD = d; best = tier; bestBound = Math.min(TOL, gap / 2);
  }
  // Strictly INSIDE the bound: a rate sitting exactly on the midpoint between two steps
  // is ambiguous, and picking one would be branch order deciding again.
  return bestD < bestBound ? best : null;
}

// ⚠ BFR IS A PROPERTY OF THE POLICY, AND A POLICY HAS A DIFFERENT `policy_id` EVERY
// CROP YEAR. `policies` is keyed (farmer, policy_number, crop_year), so MP-0926271 is
// `a1acd9af…` in CY2026 and `2e762098…` in CY2027 — a map built from last year's rows
// cannot be looked up by this year's id at all.
//
// It bit on FIRE CREEK FARMS: the caller resolved BFR from the COUNTY's own prior-year
// row, so Livingston (which had CY2026 coverage) got the bump and ONTARIO — a county
// added for CY2027 — silently got none, on the same policy. Measured cost: Ontario
// quoted at $30.28/ac where the bump makes it ~$18.17, ~$12/ac overstated, always in
// the direction that overstates a beginning farmer's premium.
//
// The STEM is the policy across years: R&H's `MP-` numbers carry no crop year, NAU's
// `NY-944-1019860-26` carries a `-YY` suffix that is stripped. Keyed on the stem, every
// county of a policy gets the one answer that is true of the insured.
//
// ⚠ STEM, NEVER FARMER. Howlett holds one standard and one BFR policy under a single
// name; rolling up to the farm reports a number that is true of neither.
function policyStem(num) {
  const s = String(num == null ? "" : num).trim().toUpperCase();
  return s ? s.replace(/-\d{2}$/, "") : "";
}
// byPolicyId: the Map deriveBfr returns. numberOf: policy_id -> policy_number.
// A stem claimed by several ids keeps the one whose tier was actually READ — an
// UNKNOWN must never displace a real observation just by arriving second.
function bfrByStem(byPolicyId, numberOf) {
  const out = new Map();
  for (const [pid, st] of byPolicyId || new Map()) {
    const stem = policyStem(numberOf ? numberOf(pid) : null);
    if (!stem || !st) continue;
    const prev = out.get(stem);
    if (!prev || (prev.tier === TIER.UNKNOWN && st.tier !== TIER.UNKNOWN)) out.set(stem, st);
  }
  return out;
}
// The one lookup both callers use: the row's own id first (exact), then the policy stem
// of whichever row we have. `fallbackRow` exists because the county's prior-year row is
// NULL for a county the grower added this year — which is precisely the case the id
// lookup cannot serve.
function bfrStatusFor(byPolicyId, byStem, row, fallbackRow, numberOf) {
  const direct = row && row.policy_id != null ? (byPolicyId && byPolicyId.get(row.policy_id)) : null;
  if (direct) return direct;
  for (const src of [row, fallbackRow]) {
    const stem = policyStem(numberOf && src && src.policy_id != null ? numberOf(src.policy_id) : null);
    const hit = stem && byStem ? byStem.get(stem) : null;
    if (hit) return hit;
  }
  return null;
}

// ONE predicate for "is this a BFR tier". The enumerations it replaces
// (`t === BFR_10 || t === BFR_15`) silently answered NO for the two interior steps the
// moment they existed — a grower in their 3rd or 4th year would have read as not a
// beginning farmer at all. A tier added later is covered by construction.
function isBfrTier(t) { return t != null && t !== TIER.NONE && t !== TIER.UNKNOWN && BONUS[t] > 0; }

// The BFR crop year a tier names, where it names one: 3rd and 4th are single years, and
// the +15/+10 tiers span a RANGE so they pin nothing. Returns null when unpinned — an
// unknown year must not become a guessed one.
function benefitYearOfTier(tier) {
  const r = TIER_YEARS[tier];
  return r && r[0] === r[1] ? r[0] : (r && r.length === 1 ? r[0] : null);
}

// Step the ladder forward. Observed year 4 in CY2026 is year 5 in CY2027, which is a
// DIFFERENT bonus — the whole reason carrying a tier forward unchanged is wrong.
function tierForYear(year) {
  const y = num(year);
  if (y == null || y < 1) return TIER.UNKNOWN;
  if (y > LAST_BENEFIT_YEAR) return TIER.NONE;     // no longer a beginning farmer
  return TIER_BY_BONUS[BONUS_BY_YEAR[y]] || TIER.UNKNOWN;
}

// ── the modal reference, built from the WHOLE book ──────────────────────────────
// Keyed on (coverage level, unit structure) because those are the two things RMA's own
// schedule keys on. A cell with fewer than MIN_PEERS distinct policies cannot establish
// a mode, and a mode of one is just the row looking at itself.
const MIN_PEERS = 5;
function modalBaseRates(bookRows, { minPeers = MIN_PEERS } = {}) {
  const cells = new Map();
  for (const r of bookRows || []) {
    if (!BASE_PLANS.has(planOf(r))) continue;
    const t = num(r.total_premium), s = num(r.subsidy);
    if (t == null || s == null || t <= 0 || s <= 0) continue;
    const cl = num(r.coverage_level);
    if (cl == null) continue;
    const key = cl.toFixed(2) + "|" + (r.unit_structure ?? r.unitStructure ?? "");
    const rate = Math.round((s / t) * 1000) / 1000;
    const c = cells.get(key) || { counts: new Map(), pol: new Set() };
    c.counts.set(rate, (c.counts.get(rate) || 0) + 1);
    c.pol.add(r.policy_id ?? r.policyId ?? "?");
    cells.set(key, c);
  }
  const out = new Map();
  for (const [key, c] of cells) {
    if (c.pol.size < minPeers) continue;
    let best = null, bestN = 0;
    for (const [rate, n] of c.counts) if (n > bestN) { best = rate; bestN = n; }
    out.set(key, { modal: best, peers: c.pol.size });
  }
  return out;
}

function tierFromModal(rows, modal) {
  // Every base row is compared against its OWN cell, then the policy takes the tier its
  // largest-premium reading supports — a policy is one insured, so a split is a signal
  // the probe is not working rather than a farm half-qualifying.
  let bestGross = 0, bestTier = null, bestDelta = null;
  for (const r of rows) {
    if (!BASE_PLANS.has(planOf(r))) continue;
    const t = num(r.total_premium), s = num(r.subsidy), cl = num(r.coverage_level);
    if (t == null || s == null || t <= 0 || s <= 0 || cl == null) continue;
    const m = modal.get(cl.toFixed(2) + "|" + (r.unit_structure ?? r.unitStructure ?? ""));
    if (!m || m.modal == null) continue;
    const d = s / t - m.modal;
    // ⚠ THE MODAL FALLBACK KEEPS THE WIDE TOL AND THEREFORE CANNOT NAME AN INTERIOR
    // STEP. Its baseline is a MODE over the book, not a published constant, so it
    // carries the mode's own scatter — resolving +10 from +11 at 0.004 against a
    // baseline that is itself uncertain would be false precision. It answers the
    // durable +10 (or +15) and says so; the flat SCO/ECO probe above is the only one
    // trusted to name a year.
    const tier = Math.abs(d - 0.15) <= TOL ? TIER.BFR_15
               : Math.abs(d - 0.10) <= TOL ? TIER.BFR_10
               : Math.abs(d) <= TOL ? TIER.NONE : null;
    if (tier && t > bestGross) { bestGross = t; bestTier = tier; bestDelta = d; }
  }
  return bestTier ? { tier: bestTier, delta: bestDelta } : null;
}

/**
 * BFR status per policy.
 *
 * @param rows      crop_coverage rows for the policies in question. Must carry
 *                  `subsidy` and `total_premium` — a query that omits them makes this
 *                  silently return UNKNOWN for the whole book, which is why
 *                  `bfrNeedsColumns` exists and the callers assert it.
 * @param bookRows  rows to build the modal reference from. Defaults to `rows`; pass the
 *                  whole loaded book where you have it, or the fallback has no peers.
 * @param cropYear  the year `rows` describe. The flat SCO/ECO baseline changed with
 *                  OBBBA, so an earlier year is refused rather than misread.
 */
function deriveBfr(rows, { bookRows = null, cropYear = null } = {}) {
  const byPolicy = new Map();
  for (const r of rows || []) {
    const k = r.policy_id ?? r.policyId ?? null;
    if (k == null) continue;
    (byPolicy.get(k) || byPolicy.set(k, []).get(k)).push(r);
  }
  const modal = modalBaseRates(bookRows || rows || []);
  const out = new Map();
  for (const [pid, prows] of byPolicy) {
    out.set(pid, deriveBfrForPolicy(prows, { modal, cropYear }));
  }
  return out;
}

function deriveBfrForPolicy(prows, { modal = new Map(), cropYear = null } = {}) {
  const yr = num(cropYear);
  // ⚠ THE BASELINE MOVED. Reading a CY2025 SCO row against 0.80 would report a 15-point
  // bump on every ordinary policy, because SCO was 65% before OBBBA.
  if (yr != null && yr < AREA_BASE_FIRST_YEAR) {
    return { tier: TIER.UNKNOWN, bonus: 0, method: "REFUSED_PRE_OBBBA", rate: null,
             why: `SCO/ECO were subsidised at 65% before CY${AREA_BASE_FIRST_YEAR}, so a rate read against 80% would invent a bump` };
  }
  // THE STATED FLAG FIRST. It settles WHETHER, not WHICH TIER — the +15 depends on
  // being inside the first two crop years, which no field carries — so the rate still
  // names the tier where it can, and the durable +10 is the floor when it cannot.
  const stated = statedBfr(prows);
  const area = pooled(prows.filter((r) => AREA_PLANS.has(planOf(r))));
  if (stated) {
    const t = area ? tierFromArea(area.rate) : null;
    if (isBfrTier(t)) {
      return { tier: t, bonus: BONUS[t], method: "STATED_WAIVER", stated, rate: area.rate, rows: area.n,
               why: `the AIP wrote Administrative Fee Waiver Code "${stated}" (P14 #32), which RMA defines as Beginning Farmer Rancher; its SCO/ECO subsidy of ${(area.rate * 100).toFixed(1)}% names the tier` };
    }
    // ⚠ STATED BFR, UNREADABLE TIER — including the case where the rate reads STANDARD.
    // Those disagree, and the file wins on WHETHER while the rate is not trusted to
    // overrule it on WHICH. Reported, never silently resolved to one or the other.
    return { tier: TIER.BFR_10, bonus: BONUS[TIER.BFR_10], method: "STATED_WAIVER_TIER_UNKNOWN",
             stated, rate: area ? area.rate : null, rows: area ? area.n : 0,
             tierDisagrees: !!(area && t == null) || (area != null && t == null),
             why: `the AIP wrote Administrative Fee Waiver Code "${stated}" (P14 #32) = Beginning Farmer Rancher` +
                  (area ? `, but its SCO/ECO subsidy reads ${(area.rate * 100).toFixed(1)}% rather than 90% or 95%, so the tier is not established and the durable +10 is used`
                        : ", and it carries no SCO/ECO to name the tier, so the durable +10 is used") };
  }
  if (area) {
    const tier = tierFromArea(area.rate);
    if (tier) {
      return { tier, bonus: BONUS[tier], method: "AREA_FLAT", rate: area.rate, rows: area.n,
               why: `SCO/ECO subsidy reads ${(area.rate * 100).toFixed(1)}% against a flat ${AREA_BASE_SUBSIDY * 100}%` };
    }
    return { tier: TIER.UNKNOWN, bonus: 0, method: "AREA_UNREADABLE", rate: area.rate, rows: area.n,
             why: `SCO/ECO subsidy reads ${(area.rate * 100).toFixed(1)}%, which is not a tier this rule can name` };
  }
  const m = tierFromModal(prows, modal);
  if (m) {
    return { tier: m.tier, bonus: BONUS[m.tier], method: "MODAL_BASE", rate: null, delta: m.delta,
             why: `no SCO/ECO on this policy; its MPCI subsidy sits ${(m.delta * 100).toFixed(1)} points off the book's modal rate for the same level and unit structure` };
  }
  return { tier: TIER.UNKNOWN, bonus: 0, method: "NO_SIGNAL", rate: null,
           why: "no SCO/ECO on this policy and no comparable peer group for its MPCI rate" };
}

// A quote seed. `bfr` is the DURABLE +10; the extra 5 is reported separately and is
// never seeded on, because the tier that earned it expires and the record cannot say
// whether the quote year is still inside it.
function bfrQuoteSeed(status, { observedYear = null, quoteYear = null } = {}) {
  const t = status && status.tier;
  const isBfr = isBfrTier(t);
  const oY = num(observedYear), qY = num(quoteYear);
  const forward = oY != null && qY != null && qY > oY;
  const step = forward ? qY - oY : 0;

  // ⚠ THE LADDER STEPS WITH THE CROP YEAR, AND THAT IS THE WHOLE POINT. A tier names a
  // BFR benefit year; next crop year is the NEXT benefit year and may be a different
  // bonus. Carrying a tier forward unchanged is what makes a quote wrong — and it is
  // what the AIP's own illustration appears to do (see the Fire Creek note below).
  const obsYear = benefitYearOfTier(t);                 // pinned only by +13 / +11
  const quotedYear = obsYear != null && step ? obsYear + step : (step ? null : obsYear);
  const steppedTier = quotedYear != null ? tierForYear(quotedYear) : null;

  // What is actually QUOTED. Where the year is pinned we step it and quote that tier.
  // Where it is not (a +15 or +10 observation spans a range of years) we fall back to
  // the durable +10, which is what this module has always done — never the higher tier,
  // because quoting a bonus a grower may not still have UNDER-states their premium on a
  // document they sign.
  const quotedTier = !isBfr ? t
    : !forward ? t
    : steppedTier && isBfrTier(steppedTier) ? steppedTier
    : steppedTier === TIER.NONE ? TIER.NONE          // past the 10th year — no longer BFR
    : TIER.BFR_10;

  return {
    bfr: isBfrTier(quotedTier),
    // Kept for callers that still speak the boolean. It can only be true where the
    // observation IS the quote year; carried forward, "first two crop years" may have
    // lapsed and the stepped ladder answers instead.
    firstTwoYears: quotedTier === TIER.BFR_15 && !forward,
    extraNeedsConfirming: t === TIER.BFR_15 && forward,
    // The benefit year to QUOTE at, for bfrBonusForYear. null means unpinned, and the
    // engine's own fallback (the durable +10) applies.
    benefitYear: quotedYear,
    quotedTier: quotedTier || TIER.UNKNOWN,
    bonus: BONUS[quotedTier] ?? 0,
    observedTier: t || TIER.UNKNOWN,
    observedYear: oY,
    observedBenefitYear: obsYear,
    // ⚠ SURFACED, NEVER SILENT. When the tier we quote differs from the tier the AIP
    // billed last year, an agent is going to be asked why the subsidy moved — and on
    // FIRE CREEK FARMS the two R&H artifacts themselves disagree: the CY2026 policy was
    // BILLED at +11 (0.6200 on BU-80 base 0.51, and 0.9102 on the flat ECO base — two
    // independent rows), which makes CY2026 their 4th benefit year and CY2027 their
    // 5th (+10); but R&H's own CY2027 Coverage Analyzer quotes +11 and prints "Years of
    // BFR Benefits Received - 3 Years". One of those is carrying a stale year count.
    // This rule steps, because stepping is what the published schedule says, and REPORTS
    // the disagreement so a human settles it with the AIP rather than a default deciding.
    steppedDown: !!(forward && isBfr && quotedTier !== t),
  };
}

// The columns this rule cannot work without. A query that omits them returns UNKNOWN
// for every policy and looks exactly like a book with no beginning farmers in it.
// The columns this rule cannot work without. A query that omits them returns UNKNOWN
// for every policy and looks exactly like a book with no beginning farmers in it.
// `admin_fee_waiver_code` is the STATED flag: omitted, the rule silently falls back to
// the derivation for everybody — which is the state this repo was in before sql/060,
// and it looks identical to working.
const bfrNeedsColumns = ["subsidy", "total_premium", "mpci_plan_code", "coverage_level", "policy_id", "admin_fee_waiver_code"];

// ── THE AGENT'S OVERRIDE, AS ONE RULE ─────────────────────────────────────────
//
// Kevin, 2026-10-13: *"I manually selected BFR, it updated premiums on the main page,
// but then when I print, the print does not reflect the BFR prices."*
//
// ⚠ THE SCREEN AND THE PRINT HAD TWO SEPARATE `bfrOptOf` FUNCTIONS AND ONLY ONE OF
// THEM KNEW ABOUT THE OVERRIDE. The print copy derives from the farm's own rows, which
// is correct and deliberate — the batch printer walks many growers through one
// function, so reading component state there would print one farm's subsidy on
// another's quote. But it had no way to be TOLD about an override either, so the agent
// ticked +15, watched every premium on screen move, and printed the underived number
// on the document the grower signs. HOWLETT FARMS: $1.79/ac on screen, $7.16/ac on
// paper, on the same 60% line.
//
// This is the mapping both copies now call. It is here rather than in App.jsx because
// a rule defined twice is a rule that drifts twice — this file already records three
// news handlers, two guarantee engines and three copies of normFarmerName doing it.
//
// `null` means "no override" — follow the derivation. Every other mode is the agent
// saying something the record does not.
// ── SELECTING BY THE YEAR OF FARMING, WHICH IS WHAT THE SCHEDULE IS KEYED ON ──
//
// Kevin, 2026-10-13: *"need to be able to select how many years of farming or BFR
// discount percentage."*
//
// ⚠ THE PERCENTAGE IS A FUNCTION OF THE YEAR, SO THE YEAR IS THE INPUT. RMA's
// post-OBBBA schedule is +15 for the 1st–2nd BFR crop years, +13 for the 3rd, +11 for
// the 4th and +10 for the 5th–10th (MGR-25-006; see the sourced block in
// src/mpciEngine.js). Offering a free-text percentage would let an agent quote +12,
// which RMA does not sell — inventing a subsidy on a document a grower signs. Picking
// the YEAR and SHOWING the percentage answers both halves of the ask and can only ever
// produce a rate that exists.
//
// ⚠ AND THE UI COULD NOT REACH TWO OF THE FOUR STEPS. The control offered Auto / Off /
// +10 / +15 only, so a grower in their 3rd or 4th BFR crop year — +13 and +11 — could
// not be quoted correctly by hand at all. Those two steps are exactly the ones the
// engine's own schedule comment records as having been invisible for months.
const BFR_YEAR_MODE = /^y(10|[1-9])$/;
const BFR_MODES = ["auto", "none", "y1", "y2", "y3", "y4", "y5", "10", "11", "13", "15"];
function bfrModeOption(mode) {
  const m = String(mode == null ? "auto" : mode);
  // Year-keyed modes. `bfrFirstTwoYears` is kept in step with the year rather than
  // passed independently — the engine prefers `bfrBenefitYear` when it is present, and
  // two fields that can disagree about the same fact is how a wrong tier gets quoted.
  const ym = BFR_YEAR_MODE.exec(m);
  if (ym) {
    const y = Number(ym[1]);
    return { bfr: true, bfrFirstTwoYears: y <= 2, bfrBenefitYear: y };
  }
  switch (m) {
    case "none": return { bfr: false, bfrFirstTwoYears: false, bfrBenefitYear: null };
    // The legacy percentage modes, kept so a saved selection and the existing fixtures
    // resolve exactly as before. Each is the FIRST year of its own step.
    case "10":   return { bfr: true,  bfrFirstTwoYears: false, bfrBenefitYear: 5 };
    case "11":   return { bfr: true,  bfrFirstTwoYears: false, bfrBenefitYear: 4 };
    case "13":   return { bfr: true,  bfrFirstTwoYears: false, bfrBenefitYear: 3 };
    case "15":   return { bfr: true,  bfrFirstTwoYears: true,  bfrBenefitYear: 1 };
    // ⚠ AN UNRECOGNISED MODE FOLLOWS THE DERIVATION, it does not switch BFR off. The
    // two are different answers — "the record says no" versus "nobody has said
    // otherwise" — and a typo must not quietly become the first one on a quote.
    default: return null;
  }
}

// What the picker offers, and what each option is worth. Built FROM the schedule so a
// label and the premium it produces cannot drift: `BONUS_BY_YEAR` is the same table
// `bfrBonusForYear` rates from.
// ⚠ YEAR 5 COVERS 5–10 and says so. A grower past their 10th BFR crop year is not a
// beginning farmer any more, which is `none`, not a sixth option.
const BFR_YEAR_CHOICES = [1, 2, 3, 4, 5].map((y) => ({
  mode: "y" + y,
  year: y,
  bonus: BONUS_BY_YEAR[y],
  label: (y === 5 ? "5th–10th" : y === 1 ? "1st" : y === 2 ? "2nd" : y === 3 ? "3rd" : y + "th"),
  pct: "+" + Math.round(BONUS_BY_YEAR[y] * 100),
}));

// The sentence a quote has to carry when a bonus was applied. Kevin, 2026-10-13:
// "Quote needs to state BFR." A sheet that silently applies a 15-point subsidy cannot
// be checked by the grower signing it or by the AIP receiving it.
// ⚠ IT NAMES WHERE THE STATUS CAME FROM. "the AIP's own file says so" and "we inferred
// it from the subsidy" and "the agent typed it" are three different claims, and only
// the first is a fact about the policy.
function bfrQuoteNote(opt, { source = null, policyNumber = null } = {}) {
  if (!opt || !opt.bfr) return null;
  const y = Number(opt.bfrBenefitYear);
  const bonus = Number.isInteger(y) && y >= 1 && y <= LAST_BENEFIT_YEAR
    ? BONUS_BY_YEAR[y] : (opt.bfrFirstTwoYears ? BONUS_BY_YEAR[1] : BONUS_BY_YEAR[5]);
  const pts = Math.round(bonus * 100);
  const yr = Number.isInteger(y) && y >= 1
    ? (y >= 5 ? "5th–10th BFR crop year" : (y === 1 ? "1st" : y === 2 ? "2nd" : y === 3 ? "3rd" : "4th") + " BFR crop year")
    : null;
  // ⚠ SHORT ENOUGH TO STAY ON ONE LINE, because this sentence rides the sheet's
  // disclaimer row and a second line costs ~13px on a page with 14px of headroom.
  // The policy number was dropped from the derived form rather than the FACT of where
  // the status came from: the sheet already prints the policy at the top, and the three
  // phrasings have to be comparable in length or only one of them fits.
  const how = String(source || "").startsWith("STATED")
      ? "the AIP's own fee-waiver code"
    : source === "agent" ? "entered by the agent"
    : source ? "derived from last year's SCO/ECO subsidy"
    : null;
  // ⚠ ONE LINE, BECAUSE IT RIDES THE SHEET'S DISCLAIMER ROW. Measured: the long form
  // wrapped to two lines and left the quote page at exactly 960px — the letter sheet's
  // limit, i.e. no headroom at all, so the next farm with one more note would print a
  // blank page again. Every word that earns its place is here and nothing else: the
  // points, the year, where the status came from, and that coverage did not change.
  return {
    pts, benefitYear: Number.isInteger(y) && y >= 1 ? y : null, basis: how,
    text: "Beginning Farmer & Rancher: +" + pts + " pts of premium subsidy applied"
      + (yr ? " (" + yr + (how ? ", " + how : "") + ")" : how ? " (" + how + ")" : "")
      + " — premium only, coverage unchanged.",
  };
}

module.exports = {
  bfrModeOption, BFR_MODES, BFR_YEAR_CHOICES, bfrQuoteNote,
  deriveBfr, deriveBfrForPolicy, bfrQuoteSeed, modalBaseRates, statedBfr, WAIVER_BFR,
  TIER, BONUS, AREA_BASE_SUBSIDY, AREA_BASE_FIRST_YEAR, AREA_PLANS, BASE_PLANS, TOL, TOL_TIER,
  TIER_YEARS, TIER_BY_BONUS, BONUS_BY_YEAR, LAST_BENEFIT_YEAR, isBfrTier, benefitYearOfTier, tierForYear,
  policyStem, bfrByStem, bfrStatusFor,
  bfrNeedsColumns,
};
